/**
 * Keyboard navigation for one menu panel.
 *
 * One instance is created per panel, root or submenu, and it owns exactly two
 * things: the keydown listener on that panel and the roving `tabindex` inside
 * it. It knows nothing about the submenu chain, the placement engine or the
 * event system; every decision that needs that context is handed back to the
 * caller through `KeyboardHandlers`.
 *
 * Ownership rule, so nothing fights over the same attribute: the renderer
 * writes `tabindex="-1"` on every row while it builds the panel, and from the
 * moment a `Keyboard` is constructed for that panel it is the only writer of
 * `tabindex` inside it. `ContextMenu` never touches it.
 *
 * The two rules worth stating out loud, because both are the opposite of the
 * obvious implementation:
 *
 * - Separators and headers are skipped, disabled rows are **not**. A disabled
 *   row that cannot be focused is a row a screen reader never announces, so the
 *   user never learns why the action is unavailable.
 * - In RTL the submenu opens towards the inline-start, which is physically the
 *   left, so `ArrowLeft` is the key that enters a submenu and `ArrowRight` is
 *   the key that leaves it. The pair simply swaps.
 */

import type { CloseReason } from './types'

export interface KeyboardHandlers {
  /** A row became the current one. `index` counts focusable rows, not items. */
  onHighlight(el: HTMLElement, index: number): void
  onActivate(el: HTMLElement): void
  onOpenSubmenu(el: HTMLElement): void
  /** Leave the current submenu. The caller decides what "current" means. */
  onCloseSubmenu(): void
  onClose(reason: CloseReason): void
}

export interface KeyboardOptions {
  /** Swaps the two arrow keys, because a submenu opens the other way. */
  rtl?: boolean
  /** How long the type-ahead buffer survives without a new character. */
  typeAheadTimeout?: number
}

/** The type-ahead buffer lifetime the WAI-ARIA menu pattern asks for. */
const TYPE_AHEAD_TIMEOUT = 500

/** `Tab` leaves the menu entirely, which is a focus loss rather than a cancel. */
const TAB_CLOSE_REASON: CloseReason = 'blur'

export class Keyboard {
  readonly #menu: HTMLElement
  readonly #handlers: KeyboardHandlers
  readonly #enterSubmenuKey: string
  readonly #leaveSubmenuKey: string
  readonly #typeAheadTimeout: number

  /** The current row's position among the focusable rows, or -1 for none. */
  #index = -1
  #buffer = ''
  #bufferTimer: ReturnType<typeof setTimeout> | null = null
  #destroyed = false

  constructor(menu: HTMLElement, handlers: KeyboardHandlers, options: KeyboardOptions = {}) {
    this.#menu = menu
    this.#handlers = handlers
    this.#enterSubmenuKey = options.rtl === true ? 'ArrowLeft' : 'ArrowRight'
    this.#leaveSubmenuKey = options.rtl === true ? 'ArrowRight' : 'ArrowLeft'
    this.#typeAheadTimeout = options.typeAheadTimeout ?? TYPE_AHEAD_TIMEOUT

    menu.addEventListener('keydown', this.#onKeyDown)

    // The roving invariant holds from the first frame: one row is the tab stop
    // even though nothing is highlighted yet, so tabbing into the panel lands
    // somewhere sensible.
    this.#rove(0)
  }

  /** Highlight and focus the first row. This is how a submenu is entered. */
  focusFirst(): void {
    this.#highlight(0)
  }

  destroy(): void {
    if (this.#destroyed) return

    this.#destroyed = true
    this.#menu.removeEventListener('keydown', this.#onKeyDown)
    this.#clearBuffer()
  }

  /** Every row a key can reach. Separators and headers are not rows. */
  #rows(): HTMLElement[] {
    return [...this.#menu.querySelectorAll<HTMLElement>('.ctx-item')]
  }

  #current(): HTMLElement | null {
    return this.#rows()[this.#index] ?? null
  }

  /** Move the single `tabindex="0"` without focusing or announcing anything. */
  #rove(index: number): void {
    const rows = this.#rows()

    for (const [position, row] of rows.entries()) row.tabIndex = position === index ? 0 : -1
  }

  #highlight(index: number): void {
    const rows = this.#rows()
    const row = rows[index]

    if (row === undefined) return

    this.#index = index
    this.#rove(index)
    row.focus({ preventScroll: true })
    this.#handlers.onHighlight(row, index)
  }

  /**
   * Step by one row, wrapping.
   *
   * With nothing highlighted yet, a step down lands on the first row and a step
   * up on the last, which is what a menu opened with the keyboard should do.
   */
  #move(delta: number): void {
    const count = this.#rows().length

    if (count === 0) return

    if (this.#index === -1) {
      this.#highlight(delta > 0 ? 0 : count - 1)

      return
    }

    this.#highlight((this.#index + delta + count) % count)
  }

  #activate(): void {
    const row = this.#current()

    if (row === null) return

    // A row with a submenu opens it rather than firing an action: there is no
    // action to fire, and this is what the menu pattern specifies.
    if (row.getAttribute('aria-haspopup') === 'menu') {
      this.#handlers.onOpenSubmenu(row)

      return
    }

    this.#handlers.onActivate(row)
  }

  #enterSubmenu(): void {
    const row = this.#current()

    // On a row without a submenu the key does nothing at all, rather than
    // falling through to some other handler.
    if (row === null || row.getAttribute('aria-haspopup') !== 'menu') return

    this.#handlers.onOpenSubmenu(row)
  }

  #labelOf(row: HTMLElement): string {
    const label = row.querySelector('.ctx-item-label')?.textContent ?? row.textContent ?? ''

    return label.trim().toLowerCase()
  }

  #clearBuffer(): void {
    if (this.#bufferTimer !== null) clearTimeout(this.#bufferTimer)

    this.#bufferTimer = null
    this.#buffer = ''
  }

  /**
   * Jump to the next row whose label starts with what has been typed.
   *
   * A single character searches from the row after the current one, so pressing
   * the same letter repeatedly cycles through the rows that share it. Anything
   * longer searches from the current row, so continuing to type refines the
   * match instead of skipping past it.
   */
  #typeAhead(char: string): void {
    const next = this.#buffer + char.toLowerCase()
    const repeated = next.length > 1 && [...next].every((entry) => entry === next[0])
    const query = repeated ? char.toLowerCase() : next

    this.#buffer = next

    if (this.#bufferTimer !== null) clearTimeout(this.#bufferTimer)

    this.#bufferTimer = setTimeout(() => {
      this.#bufferTimer = null
      this.#buffer = ''
    }, this.#typeAheadTimeout)

    const rows = this.#rows()

    if (rows.length === 0) return

    const from = query.length === 1 ? this.#index + 1 : Math.max(this.#index, 0)

    for (let step = 0; step < rows.length; step += 1) {
      const index = (from + step + rows.length) % rows.length
      const row = rows[index]

      if (row !== undefined && this.#labelOf(row).startsWith(query)) {
        this.#highlight(index)

        return
      }
    }
  }

  readonly #onKeyDown = (event: Event): void => {
    if (this.#destroyed || !(event instanceof KeyboardEvent)) return

    // A modified arrow belongs to the browser or the operating system. Shift is
    // the exception, because Shift+Tab is still a Tab.
    if (event.altKey || event.ctrlKey || event.metaKey) return

    const { key } = event
    const consume = (): void => {
      event.preventDefault()
      event.stopPropagation()
    }

    switch (key) {
      case 'ArrowDown':
        consume()
        this.#move(1)

        return
      case 'ArrowUp':
        consume()
        this.#move(-1)

        return
      case 'Home':
        consume()
        this.#highlight(0)

        return
      case 'End':
        consume()
        this.#highlight(this.#rows().length - 1)

        return
      case 'Enter':
        consume()
        this.#activate()

        return
      case 'Escape':
        consume()
        this.#clearBuffer()
        this.#handlers.onClose('escape')

        return
      case 'Tab':
        consume()
        this.#clearBuffer()
        this.#handlers.onClose(TAB_CLOSE_REASON)

        return
      default:
        break
    }

    if (key === this.#enterSubmenuKey) {
      consume()
      this.#enterSubmenu()

      return
    }

    if (key === this.#leaveSubmenuKey) {
      consume()
      this.#handlers.onCloseSubmenu()

      return
    }

    // Space activates, unless a type-ahead is in progress: a label with a space
    // in it must be typeable, and a menu with nothing typed must be operable
    // with the key every other widget activates with.
    if (key === ' ' && this.#buffer === '') {
      consume()
      this.#activate()

      return
    }

    if (key.length === 1) {
      consume()
      this.#typeAhead(key)
    }
  }
}
